<template>
    <div>
        <div v-for="(group, groupIndex) in conditions" :key="group.id" class="group-container">
            <div class="text-right btn-delete-group-wrapper">
                <button class="btn btn-outline-danger btn-delete-group"
                        @click="deleteGroup(groupIndex)"
                        :disabled="!canWrite"
                >
                    <i class="far fa-trash-alt"></i>
                </button>

                <div class="overlay-mask" />
                <div class="overlay-border" :class="{ disabled: !canWrite }" />
            </div>
            
            <brick-filter-condition v-for="(condition, conditionIndex) in group.children"
                :key="condition.id"
                ref="conditions"

                :field="condition.field"
                :value="condition.value"
                :can-write="canWrite"
                :included-category-filters="includedCategoryFilters"
                :excluded-category-filters="excludedCategoryFilters"

                @update:value="setValue(condition, $event)"
                @update:field="setField(condition, $event)"
                                    
                @deleteCondition="deleteCondition(groupIndex, conditionIndex)"

                class="group-item"
            />

            <button v-if="canWrite"
                    class="btn btn-outline-primary btn btn-add-condition"
                    @click="addCondition(groupIndex)"
            >
                + {{resource('JourneyComposer_FilterModal_AddCondition')}}
            </button>
        </div>

        <button v-if="canWrite"
                class="btn btn-outline-primary btn btn-add-group"
                @click="addGroup()"
        >
            + {{resource('JourneyComposer_FilterModal_AddGroup')}}
        </button>
    </div>
</template>

<script>
import {guid} from "@core/Util/guid";
import promptYesNo from "@modules/JourneyBuilder/Util/promptYesNo";

import resourceMixin from '@core/Util/Mixins/resources'
import permissionsMixin from '@modules/JourneyBuilder/Mixins/journey-permissions';

import BrickFilterCondition from "./BrickFilterCondition";

async function optionalPromptYesNo(body){
    if(!body){
        return true;
    }
    
    try {
        await promptYesNo(null, body);
        return true;
    } catch(e) {
        return false;
    }
}

export default {
    name: "BrickFilterSettingsEditor",
    components: {
        BrickFilterCondition
    },
    mixins: [ resourceMixin, permissionsMixin ],
    props: {
        conditions: {
            type: Array,
            required: true
        },

        includedCategoryFilters: {
            type: Array,
            default: () => []
        },
        
        excludedCategoryFilters: {
            type: Array,
            default: () => []
        },

        deleteConditionConfirmationMessage: {
            type: String,
            required: false
        },

        deleteGroupConfirmationMessage: {
            type: String,
            required: false
        }
    },
    
    created() {
        if(this.conditions.length === 0){
            this.addGroup();
        }
    },
    
    methods: {
        setValue(condition, value) {
            condition.value = value;
            this.triggerChange();
        },
        
        setField(condition, field) {
            condition.field = field;
            this.triggerChange();
        },
        
        addCondition(groupIndex) {
            this.conditions[groupIndex].children.push(this.createBaseCondition());
            this.triggerChange();
        },

        addGroup() {
            this.conditions.push({ id: guid(), children: [ this.createBaseCondition() ] });
            this.triggerChange();
        },

        async deleteCondition(groupIndex, conditionIndex) {
            if(!(await optionalPromptYesNo(this.deleteConditionConfirmationMessage))){
                return;
            }
            
            if (this.conditions[groupIndex].children.length === 1) {
                this.conditions.splice(groupIndex, 1);
            } else {
                this.conditions[groupIndex].children.splice(conditionIndex, 1);
            }
            this.triggerChange();
        },

        async deleteGroup(groupIndex) {
            if(!(await optionalPromptYesNo(this.deleteGroupConfirmationMessage))){
                return;
            }
            
            this.conditions.splice(groupIndex, 1);
            this.triggerChange();
        },

        triggerChange() {
            this.$emit("conditions-changed", this.conditions);
        },

        createBaseCondition: () => ({
            "id": guid(),
            "field": null,
            "value": null
        }),

        displayErrors(){
            this.$refs.conditions.forEach(c => c.displayErrors());
        }
    }
}
</script>

<style scoped lang="scss">
    .group-container {
        $group-offset: 10%;
    
        border-width: 2px;
        border-color: #D5D9E0;
        border-style: solid;
    
        .btn-add-condition {
            position: relative;
            left: $group-offset;
    
            margin-top: 10px;
            margin-bottom: 10px;
        }
    
        .group-item {
            position: relative;
    
            margin-bottom: 8px;
        }
    
        margin-bottom: 10px;
    }

    .btn-delete-group-wrapper {
        position: relative;
    
        .btn-delete-group {
            height: 2.4rem;
            width: 2.4rem;
            border-radius: 0;
    
            i {
                position: absolute;
                top: 0.2rem;
                right: 0.3rem;
            }
    
            //Disabled - doesn't work with our triangle overlay 
            box-shadow: none !important;
        }
    
        .overlay-border {
            position: absolute;
            top: 0;
            right: 2.3rem;
            height: 3.4rem;
            border-right: 1px solid #DC3545;
            transform: rotate(-45deg);
            transform-origin: top;
    
            &.disabled {
                border-right-color: #E87C86;
            }
        }
    
        //Create two small triangles, align them to take a slice out of the button
        //our div may not overlap with our button, this would be prevent clicking the button 
        .overlay-mask {
            top: 0;
            right: 2.8rem;
            position: absolute;
    
            &:after {
                content: '';
                position: absolute;
                top: 1.5rem;
                border-top: 2rem solid white;
                border-right: 2rem solid transparent;
                transform: rotate(45deg);
                left: 0.5rem;
            }
    
            &:before {
                content: '';
                position: absolute;
                top: 0.3rem;
                border-top: 2.1rem solid white;
                border-right: 2.1rem solid transparent;
                transform: rotate(135deg);
                left: -0.7rem;
            }
        }
    }

    .btn-add-group {
        width: 100%;
    }
</style>